import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './Button';
import { MapPin, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const Hero: React.FC = () => {
  const navigate = useNavigate();

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-24 pb-12 overflow-hidden bg-white">
      {/* Abstract Grid Background */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="absolute left-0 right-0 top-0 -z-10 m-auto h-[310px] w-[310px] rounded-full bg-green-400 opacity-20 blur-[100px]"></div>
        <div className="absolute right-0 top-0 -z-10 h-full w-1/3 bg-gradient-to-l from-green-50/50 to-transparent"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center">
        
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-green-100 shadow-sm text-green-700 text-sm font-medium">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-600"></span>
            </span>
            Now Live for Ahmedabad Region (AUDA)
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-5xl md:text-7xl font-extrabold text-gray-900 tracking-tight mb-6 max-w-5xl leading-[1.1]"
        >
          Unified Town <br className="hidden md:block" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-700 via-green-600 to-green-500">
            Planning Insights
          </span>
        </motion.h1>

        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-lg md:text-xl text-gray-600 max-w-2xl mb-10 leading-relaxed"
        >
          Instantly analyze land feasibility. We combine AnyRoR, AUDA maps, and GDCR regulations into one actionable platform for developers.
        </motion.p>

        {/* Search Simulation */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="w-full max-w-xl relative mb-12 group"
        >
            <div className="absolute -inset-1 bg-gradient-to-r from-green-600 to-green-400 rounded-lg blur opacity-25 group-hover:opacity-50 transition duration-200"></div>
            <div className="relative bg-white rounded-lg shadow-xl p-2 flex items-center gap-2 border border-gray-100">
                <div className="pl-3 text-gray-400">
                    <MapPin size={20} />
                </div>
                <input 
                    type="text" 
                    placeholder="Enter TP Scheme No, Village, or FP Number..." 
                    className="flex-1 p-2 bg-transparent outline-none text-gray-700 placeholder:text-gray-400"
                />
                <Button className="shadow-md bg-gray-900 hover:bg-black" onClick={() => navigate('/map')}>
                    Analyze <ArrowRight size={16} className="ml-2"/>
                </Button>
            </div>
            <div className="text-left mt-3 pl-2 flex gap-3 text-xs text-gray-500">
                <span>Try:</span>
                <span className="underline cursor-pointer hover:text-primary">Thaltej TP 34</span>
                <span className="underline cursor-pointer hover:text-primary">Gota TP 12</span>
                <span className="underline cursor-pointer hover:text-primary">FP 42</span>
            </div>
        </motion.div>

        {/* Dashboard Preview Image */}
        <motion.div
           initial={{ opacity: 0, scale: 0.95, y: 40 }}
           animate={{ opacity: 1, scale: 1, y: 0 }}
           transition={{ duration: 0.8, delay: 0.5 }}
           className="relative w-full max-w-6xl"
        >
            <div className="rounded-xl bg-gray-900/5 p-2 backdrop-blur-sm border border-gray-200/50">
                <div className="rounded-lg overflow-hidden shadow-2xl border border-gray-200 bg-white relative aspect-[16/9] md:aspect-[21/9]">
                     <div className="absolute inset-0 bg-gray-50">
                        {/* Simplified UI Representation */}
                        <div className="flex h-full">
                            <div className="w-64 border-r border-gray-200 bg-white hidden lg:block p-4 space-y-4">
                                <div className="h-4 bg-gray-100 rounded w-1/2"></div>
                                <div className="space-y-2">
                                    <div className="h-12 bg-green-50 rounded border border-green-100"></div>
                                    <div className="h-12 bg-gray-50 rounded border border-gray-100"></div>
                                    <div className="h-12 bg-gray-50 rounded border border-gray-100"></div>
                                </div>
                            </div>
                            <div className="flex-1 relative overflow-hidden bg-white">
                                {/* Map Pattern */}
                                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=2400')] bg-cover bg-center opacity-20 mix-blend-multiply grayscale"></div>
                                <div className="absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="relative">
                                        <div className="w-32 h-32 border-4 border-green-600 bg-green-600/20 rounded-lg transform rotate-12 z-10 backdrop-blur-sm"></div>
                                        <div className="absolute -top-12 -right-24 bg-white p-3 rounded-lg shadow-lg border border-gray-100 w-48 animate-bounce duration-[3000ms]">
                                            <div className="text-xs font-bold text-gray-900">Final Plot #104</div>
                                            <div className="flex gap-2 mt-1">
                                                <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] rounded font-medium">Res Zone 1</span>
                                                <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] rounded font-medium">FSI 2.7</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                     </div>
                </div>
            </div>
        </motion.div>
      </div>
    </section>
  );
};
